// Engine-Anbindung: WebSocket zum Server, der Stockfish nativ rechnet.
// Formeln für Gewinnchance aus lila (ui/lib/src/ceval/winningChances.ts).
import { t } from './i18n';

export interface EngineLine {
  multipv: number;
  depth: number;
  cp: number | null;
  mate: number | null;
  pv: string[];
  nodes: number;
  nps: number;
}

export interface Eval {
  fen: string;
  lines: EngineLine[];
  done: boolean;
}

type Msg =
  | { type: 'engine'; name: string; available: boolean; path: string }
  | { type: 'lines'; fen: string; lines: EngineLine[] }
  | { type: 'done'; fen: string; bestmove: string }
  | { type: 'error'; error: string };

export class Ceval {
  name = '';
  available = true;
  error = '';
  current: Eval | null = null;
  private ws: WebSocket | null = null;
  private pending: string | null = null;

  constructor(
    private redraw: () => void,
    private opts: () => { multiPv: number; depth: number },
  ) {}

  private connect() {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/engine`);
    ws.onopen = () => {
      if (this.pending) this.send(this.pending);
      this.pending = null;
    };
    ws.onmessage = e => this.onMessage(JSON.parse(e.data) as Msg);
    ws.onclose = () => {
      this.ws = null;
    };
    this.ws = ws;
  }

  private send(fen: string) {
    const { multiPv, depth } = this.opts();
    this.ws!.send(JSON.stringify({ type: 'start', fen, multiPv, depth }));
  }

  private onMessage(m: Msg) {
    if (m.type === 'engine') {
      this.name = m.name;
      this.available = m.available;
      if (!m.available) this.error = t('Stockfish nicht gefunden ({p}).', { p: m.path });
    } else if (m.type === 'error') {
      this.error = t(m.error);
    } else if (this.current && m.fen === this.current.fen) {
      if (m.type === 'lines') {
        // Zeilen einer Tiefe kommen nacheinander; die neueste je Variante behalten
        const byPv = new Map(this.current.lines.map(l => [l.multipv, l]));
        for (const l of m.lines) byPv.set(l.multipv, l);
        this.current.lines = [...byPv.values()].sort((a, b) => a.multipv - b.multipv);
      } else if (m.type === 'done') {
        this.current.done = true;
      }
    }
    this.redraw();
  }

  start(fen: string) {
    if (this.current?.fen === fen) return;
    this.current = { fen, lines: [], done: false };
    this.error = '';
    if (this.ws?.readyState === WebSocket.OPEN) this.send(fen);
    else {
      this.pending = fen;
      this.connect();
    }
  }

  restart() {
    const fen = this.current?.fen;
    this.current = null;
    if (fen) this.start(fen);
  }

  stop() {
    this.current = null;
    this.pending = null;
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'stop' }));
  }
}

const rawWinningChances = (cp: number) => 2 / (1 + Math.exp(-0.00368208 * cp)) - 1;

/** Gewinnchance für Weiß, −1 … +1, wie lila */
export function winningChances(l: Pick<EngineLine, 'cp' | 'mate'>): number {
  if (l.mate !== null) {
    const cp = (21 - Math.min(10, Math.abs(l.mate))) * 100;
    return rawWinningChances(l.mate > 0 ? cp : -cp);
  }
  return rawWinningChances(Math.max(-1000, Math.min(1000, l.cp ?? 0)));
}

/** "+0.4", "-1.2", "#3", "#-2" aus Sicht von Weiß, wie lila renderEval (ui/lib/src/ceval/util.ts) */
export function formatEval(l: Pick<EngineLine, 'cp' | 'mate'>): string {
  if (l.mate !== null) return `#${l.mate}`;
  const e = Math.max(Math.min(Math.round((l.cp ?? 0) / 10) / 10, 99), -99);
  return (e > 0 ? '+' : '') + e.toFixed(1);
}
